
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

export const useLeadSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const sendSystemMessage = async (sessionId: string, message: string) => {
    // Hard-coded system message simulation
    console.log('System message sent:', { sessionId, message });
  };

  const submitLead = async (contactInfo: string, sourceForm: string) => {
    setIsSubmitting(true);
    try {
      // Parse contact info to extract individual fields
      const parseContactInfo = (info: string) => {
        const parts = info.split(' | ');
        let phone = '';
        let name = '';
        let email = '';
        
        // Handle different formats
        if (parts.length === 1) {
          // Single phone number or email
          if (info.includes('@')) {
            email = info;
          } else {
            phone = info;
          }
        } else if (parts.length === 2) {
          // Name - Email format or Phone | Name format
          if (parts[1].includes('@')) {
            name = parts[0];
            email = parts[1];
          } else {
            phone = parts[0];
            name = parts[1];
          }
        } else if (parts.length === 3) {
          // Phone | Name | Email format
          phone = parts[0];
          name = parts[1];
          email = parts[2];
        }
        
        return { phone, name, email };
      };

      const { phone, name, email } = parseContactInfo(contactInfo);

      // Get or create session ID
      let sessionId = localStorage.getItem('visitor_session_id');
      if (!sessionId) {
        sessionId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('visitor_session_id', sessionId);
      }

      // Hard-coded data storage simulation
      console.log('Form submission data:', { sessionId, phone, name, email, sourceForm });

      // Log the submission locally
      console.log('Website help request submitted:', { contactInfo, sourceForm });

      // Try to send notifications but don't fail if they don't work
      try {
        // Send to Telegram
        const telegramMessage = `Website help request: ${contactInfo}`;
        const telegramUrl = `https://api.telegram.org/bot7701707045:AAHWSwzh6MtWd_tCf2-Pl1OXuiix7FUbGCg/sendMessage?chat_id=8143869475&text=${encodeURIComponent(telegramMessage)}`;
        
        await fetch(telegramUrl, {
          method: 'GET',
          mode: 'no-cors' // This prevents CORS errors but we won't get response
        });
      } catch (error) {
        console.log('Telegram notification failed (this is expected due to CORS):', error);
      }

      try {
        // Send to Pushbullet
        await fetch('https://api.pushbullet.com/v2/pushes', {
          method: 'POST',
          mode: 'no-cors', // This prevents CORS errors but we won't get response
          headers: {
            'Access-Token': 'o.BWPDbkbrsIilVniba79dor9rwjbmEUk5',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'note',
            title: 'Website Help Request',
            body: `Contact info submitted: ${contactInfo}`
          })
        });
      } catch (error) {
        console.log('Pushbullet notification failed (this is expected due to CORS):', error);
      }

      // Simulate a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));

      toast({
        title: "Request Submitted!",
        description: "We'll call you back within 10 minutes.",
        className: "bg-emerald-50 border-emerald-200"
      });
      
      return true;
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to submit your details. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCallback = async (callbackData: any) => {
    try {
      setIsSubmitting(true);
      
      // Hard-coded callback submission simulation
      console.log('Callback submitted:', callbackData);
      
      toast({
        title: "Callback Scheduled",
        description: "Your callback has been scheduled successfully!",
      });

      return { success: true, callback: callbackData };
    } catch (error) {
      console.error('Error submitting callback:', error);
      toast({
        title: "Error",
        description: "Failed to schedule callback. Please try again.",
        variant: "destructive"
      });
      return { success: false, error };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitLead, submitCallback, isSubmitting };
};
